/**
 * The two end-of-escalation rows agents can see — `assignment_auto_completed` and
 * `escalation_completed` — read as a plain phrase, never a call SID, a Twilio status or sweep
 * detail (ENG-3821, Chuck, 2026-10-22). Shared by the writer (server-helpers) and the timeline
 * (chat-ui), which derives the agent-facing label itself so rows written before this change
 * read the same way. Pure, for tests.
 */

export type VoiceAssignmentEndCause = "call_ended" | "agent_leg_failed" | "sweep";

export function voiceAssignmentEndPhrase(cause: VoiceAssignmentEndCause): string {
  switch (cause) {
    case "call_ended":
      return "the call ended";
    case "agent_leg_failed":
      return "the agent never joined";
    case "sweep":
      return "the call was found to be over";
  }
}

/** Rows written before `cause` existed carry only the raw reason; its prefix says which cause it was. */
export function voiceAssignmentEndCauseFromReason(reason: unknown): VoiceAssignmentEndCause {
  if (typeof reason === "string") {
    if (reason.startsWith("agent leg")) return "agent_leg_failed";
    if (reason.startsWith("sweep")) return "sweep";
  }
  return "call_ended";
}

const AGENT_FACING_HEADINGS: Record<string, string> = {
  assignment_auto_completed: "Voice assignment completed",
  escalation_completed: "Escalation ended"
};

/**
 * The label a non-admin viewer reads for an escalation event, or `null` when the row's stored
 * text is already fit for agents. The admin view always shows the stored text.
 */
export function agentFacingEscalationLabel(subType: unknown, metadata: Record<string, unknown> | undefined): string | null {
  if (typeof subType !== "string") return null;
  const heading = AGENT_FACING_HEADINGS[subType];
  if (!heading) return null;
  const cause = metadata?.["cause"];
  const resolved: VoiceAssignmentEndCause =
    cause === "call_ended" || cause === "agent_leg_failed" || cause === "sweep" ? cause : voiceAssignmentEndCauseFromReason(metadata?.["reason"]);
  return `${heading} — ${voiceAssignmentEndPhrase(resolved)}`;
}
